const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/* URLs that must always execute immediately instead of entering the admin queue. */
const SKIP_PATTERNS = [
    '/admin/',      // ghost enter/leave, admin actions
    '/users/me',    // auth refresh
    '/auth/',       // login/register
    '/users/sidebar-order', // sidebar preference
    '/game-server/',  // real-time server operations (start-prefire, stop-personal, etc.)
    '/chunked-upload/', // transport protocol: init, chunks and completion must execute in order
    '/stratsync-lineups', // allow admins to manage global lineups instantly
    '/personal/',   // do not intercept personal space actions
    // Badge grouping. Every mutating route on this controller is AdminGuard-only
    // but lives under /achievements rather than /admin, so it slipped past the
    // '/admin/' rule above: creating a group got a fake 200 and never reached the
    // server, which read as "Create group does nothing".
    '/achievements/',
];

/** Parse URLs once so host names, query strings and fragments never masquerade as routes. */
function getEndpointPath(url) {
    try {
        return new URL(url, 'https://ghost.invalid').pathname.replace(/^\/api(?=\/|$)/, '') || '/';
    } catch {
        return ''; // Invalid mutation URLs stay reviewable rather than gaining an exemption.
    }
}

export function isLineupMediaRead(method, url) {
    return method.toLowerCase() === 'post' && ['/lineups/media-urls', '/personal/lineups/media-urls', '/stratsync-lineups/media-urls'].includes(getEndpointPath(url));
}

export function shouldInterceptGhostChange(method, url) {
    const normalizedMethod = method.toLowerCase();
    if (!MUTATION_METHODS.has(normalizedMethod)) return false;
    if (isLineupMediaRead(method, url)) return false;
    const path = getEndpointPath(url);
    const route = path.replace(/\/+$/, '');
    // These fleet operations are AdminGuard-only, but are outside /admin.
    // They must return their real provisioning result, never a queued fake success.
    if (normalizedMethod === 'post' && ['/dedicated-server/inspect', '/dedicated-server/assign', '/dedicated-server/recover', '/dedicated-server/return-to-pool'].includes(route)) return false;
    // Global catalog writes are AdminGuard-only and must return real persisted IDs.
    // Keep POST /global-strats/:id/copy queued: that operation changes a team's Stratbook.
    if (normalizedMethod === 'post' && /^\/global-strats(?:\/export-(?:scout|replay)-round)?$/.test(route)) return false;
    if (['put', 'delete'].includes(normalizedMethod) && /^\/global-strats\/[^/]+(?:\/dryrun-settings)?$/.test(route)) return false;
    // Checkout and billing portal requests must return a real Stripe redirect URL.
    // Queueing them replaces the response with the ghost adapter's synthetic
    // success payload, so the admin preview can never leave the Premium page.
    if (normalizedMethod === 'post' && ['/stripe/checkout', '/stripe/portal'].includes(route)) return false;
    // This operation builds analysis artifacts; it does not edit the team's Stratbook.
    // Match only this POST route. Sibling routes and all other mutation methods stay queued.
    if (normalizedMethod === 'post' && route === '/own-team-analysis/analyze') return false;
    return !SKIP_PATTERNS.some(pattern => path.includes(pattern));
}

const ENDPOINT_LABELS = {
    '/teams/kick': 'Kick member',
    '/teams/transfer-ownership': 'Transfer ownership',
    '/teams/update-role': 'Change role',
    '/teams/update-cs-role': 'Change CS role',
    '/teams/toggle-manager-permission': 'Toggle manager permission',
    '/teams/set-semi-admin': 'Toggle semi-admin',
    '/teams/regenerate-code': 'Regenerate invite code',
    '/teams/sync-faceit': 'Sync Faceit data',
    '/teams/logo': 'Update team logo',
    '/teams/server-ip': 'Update server IP',
    '/teams/teamspeak-ip': 'Update TeamSpeak IP',
    '/teams/details': 'Update team details',
    '/teams/map-pool': 'Update map pool',
    '/teams/map-status': 'Update map status',
    '/strats/slots': 'Modify stratbook slot',
    '/strats': 'Modify strategy',
    '/leagues': 'Modify league',
    '/matches': 'Modify match',
    '/demos/analyze': 'Analyze demo',
    '/own-team-analysis/analyze': 'Analyze own team',
    '/telegram': 'Modify Telegram integration',
    '/game-server/start-dryrun': 'Start dryrun server',
    '/game-server/stop': 'Stop game server',
};


function matchesEndpoint(path, endpoint) {
    return path === endpoint || path.startsWith(endpoint + '/');
}

export function getLabelForUrl(method, url) {
    const upperMethod = method.toUpperCase();
    const path = getEndpointPath(url);
    for (const [endpoint, label] of Object.entries(ENDPOINT_LABELS)) {
        if (matchesEndpoint(path, endpoint)) {
            if (upperMethod === 'DELETE') {
                if (matchesEndpoint(path, '/teams/logo')) return 'Delete team logo';
                if (matchesEndpoint(path, '/strats/slots')) return 'Delete stratbook slot';
                if (matchesEndpoint(path, '/strats')) return 'Delete strategy';
                if (matchesEndpoint(path, '/leagues')) return 'Delete league';
                if (matchesEndpoint(path, '/matches')) return 'Delete match';
                if (matchesEndpoint(path, '/telegram')) return 'Remove Telegram integration';
            }
            return label;
        }
    }
    const verb = { POST: 'Create', PUT: 'Update', PATCH: 'Update', DELETE: 'Delete' }[upperMethod] || 'Change';
    const resourcePath = path.replace(/\/[a-f0-9-]{36}(?=\/|$)/gi, '').replace(/\/$/, '');
    return `${verb} ${resourcePath.split('/').pop() || 'resource'}`;
}
